import React from 'react';
import { Helmet } from 'react-helmet';
import {
  Routes, Route, Navigate, useLocation,
} from 'react-router-dom';

import { useIntl } from '@edx/frontend-platform/i18n';
import { logError } from '@edx/frontend-platform/logging';
import { initializeHotjar } from '@edx/frontend-enterprise-hotjar';

import { ErrorPage, AppContext } from '@edx/frontend-platform/react';
import FooterSlot from '@openedx/frontend-slot-footer';
import { Alert } from '@openedx/paragon';

import { RequestKeys } from 'data/constants/requests';
import store from 'data/store';
import {
  selectors,
  actions,
} from 'data/redux';
import { reduxHooks } from 'hooks';
import Dashboard from 'containers/Dashboard';
import ZendeskFab from 'components/ZendeskFab';
import { ExperimentProvider } from 'ExperimentContext';

import track from 'tracking';

import { learnerDashboardData, emptyLearnerDashboardData } from 'data/services/lms/fakeData/learnerDashboard';

import AppWrapper from 'containers/WidgetContainers/AppWrapper';
import LearnerDashboardHeader from 'containers/LearnerDashboardHeader';

// ADD THESE IMPORTS FOR YOUR STUDY GUIDES
import StudyGuide1 from 'pages/StudyGuide1';
import StudyGuide3 from 'pages/StudyGuide3';
import StudyGuide4 from 'pages/StudyGuide4';
import StudyGuide5 from 'pages/StudyGuide5';
import StudyGuide6 from 'pages/StudyGuide6';
import StudyGuide7 from 'pages/StudyGuide7';
import StudyGuide from 'pages/StudyGuide'; // This is the generic study guide page that will render based on the slug in the URL
// import StudyGuide6 from 'pages/StudyGuide6';

import { getConfig } from '@edx/frontend-platform';
import messages from './messages';
import './App.scss';

export const App = () => {
  const { authenticatedUser } = React.useContext(AppContext);
  const { formatMessage } = useIntl();
  const location = useLocation();

  const isFailed = {
    initialize: reduxHooks.useRequestIsFailed(RequestKeys.initialize),
    refreshList: reduxHooks.useRequestIsFailed(RequestKeys.refreshList),
  };
  const hasNetworkFailure = isFailed.initialize || isFailed.refreshList;
  const { supportEmail } = reduxHooks.usePlatformSettingsData();
  const loadData = reduxHooks.useLoadData();

  const optimizelyScript = () => {
    if (getConfig().OPTIMIZELY_URL) {
      return <script src={getConfig().OPTIMIZELY_URL} />;
    } if (getConfig().OPTIMIZELY_PROJECT_ID) {
      return (
        <script
          src={`${getConfig().MARKETING_SITE_BASE_URL}/optimizelyjs/${getConfig().OPTIMIZELY_PROJECT_ID}.js`}
        />
      );
    }
    return null;
  };

  React.useEffect(() => {
    if (authenticatedUser?.administrator || getConfig().NODE_ENV === 'development') {
      window.loadEmptyData = () => {
        loadData(emptyLearnerDashboardData);
      };
      window.loadMockData = () => {
        loadData(learnerDashboardData);
      };
      window.store = store;
      window.selectors = selectors;
      window.actions = actions;
      window.track = track;
    }
    if (getConfig().HOTJAR_APP_ID) {
      try {
        initializeHotjar({
          hotjarId: getConfig().HOTJAR_APP_ID,
          hotjarVersion: getConfig().HOTJAR_VERSION,
          hotjarDebug: !!getConfig().HOTJAR_DEBUG,
        });
      } catch (error) {
        logError(error);
      }
    }
  }, [authenticatedUser, loadData]);

  return (
    <>
      <Helmet>
        <title>{formatMessage(messages.pageTitle)}</title>
        <link rel="shortcut icon" href={getConfig().FAVICON_URL} type="image/x-icon" />
        {optimizelyScript()}
      </Helmet>
      <div>
        <AppWrapper>
          <LearnerDashboardHeader />
          <main>
            <Routes>
              <Route
                path="/"
                element={
                  hasNetworkFailure
                    ? (
                      <Alert variant="danger">
                        <ErrorPage message={formatMessage(messages.errorMessage, { supportEmail })} />
                      </Alert>
                    ) : (
                      <ExperimentProvider>
                        <Dashboard />
                      </ExperimentProvider>
                    )
                }
              />
              <Route path="study-guide/:slug" element={<StudyGuide />} />
              <Route path="*" element={<Navigate to="/learner-dashboard/" replace />} />
            </Routes>
          </main>
        </AppWrapper>
        {/* <FooterSlot /> */}
        <ZendeskFab />
      </div>
    </>
  );
};

export default App;
